import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  Scale,
  Newspaper,
  Phone,
  Globe,
  MapPin,
  Clock,
  ExternalLink,
  Search,
  Loader2,
  Info,
  AlertTriangle,
  Languages,
  Eye,
  ShieldAlert,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { toast } from "sonner";
import BrandMark from "@/components/BrandMark";
import DisclaimerBanner from "@/components/DisclaimerBanner";

const TABS = [
  { id: "consulates", label: "Consulados", Icon: Building2, color: "blue" },
  { id: "legal", label: "Abogados Pro Bono", Icon: Scale, color: "emerald" },
  { id: "news", label: "Noticias", Icon: Newspaper, color: "amber" },
];

export default function Recursos() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("consulates");
  const [consulates, setConsulates] = useState([]);
  const [countries, setCountries] = useState([]);
  const [filterCountry, setFilterCountry] = useState("all");
  const [search, setSearch] = useState("");
  const [legal, setLegal] = useState([]);
  const [news, setNews] = useState({ sources: [], raid_guidance: null });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [c, l, n] = await Promise.all([
          api.get("/resources/consulates"),
          api.get("/resources/legal-help"),
          api.get("/resources/news"),
        ]);
        setConsulates(c.data.consulates);
        setCountries(c.data.countries);
        setLegal(l.data.organizations);
        setNews(n.data);
      } catch {
        toast.error("No se pudieron cargar los recursos");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filteredConsulates = useMemo(() => {
    return consulates.filter((c) => {
      if (filterCountry !== "all" && c.country !== filterCountry) return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          c.country.toLowerCase().includes(q) ||
          c.city.toLowerCase().includes(q) ||
          (c.address || "").toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [consulates, filterCountry, search]);

  return (
    <div className="min-h-screen bg-zinc-50 pb-28">
      <div className="max-w-3xl mx-auto w-full px-5 pt-6">
        <button
          onClick={() => navigate("/dashboard")}
          className="flex items-center gap-2 text-sm font-semibold text-zinc-600 mb-4 hover:text-zinc-900"
          data-testid="recursos-back"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver
        </button>

        <div className="mb-6">
          <BrandMark size="sm" />
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-zinc-900 leading-tight mt-4">
            Centro de Recursos
          </h1>
          <p className="text-base text-zinc-700 mt-2 leading-relaxed">
            Consulados latinos en EE.UU., organizaciones que dan ayuda legal gratis, y fuentes confiables de noticias migratorias.
          </p>
        </div>

        {/* Tabs */}
        <div className="grid grid-cols-3 gap-2 mb-6 bg-zinc-100 p-1 rounded-xl">
          {TABS.map((t) => {
            const active = tab === t.id;
            const { Icon } = t;
            return (
              <button
                key={t.id}
                data-testid={`recursos-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`h-12 rounded-lg font-bold text-sm flex items-center justify-center gap-2 transition-all ${
                  active ? "bg-white text-zinc-900 shadow" : "text-zinc-500 hover:text-zinc-700"
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="hidden sm:inline">{t.label}</span>
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
          </div>
        ) : (
          <>
            {tab === "consulates" && (
              <ConsulatesTab
                items={filteredConsulates}
                countries={countries}
                filterCountry={filterCountry}
                setFilterCountry={setFilterCountry}
                search={search}
                setSearch={setSearch}
              />
            )}
            {tab === "legal" && <LegalTab items={legal} />}
            {tab === "news" && <NewsTab sources={news.sources} guidance={news.raid_guidance} />}
          </>
        )}
      </div>
      <DisclaimerBanner />
    </div>
  );
}

/* -------------------- CONSULATES TAB -------------------- */
function ConsulatesTab({ items, countries, filterCountry, setFilterCountry, search, setSearch }) {
  return (
    <div>
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 flex gap-2 items-start">
        <Info className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
        <p className="text-xs text-amber-900 leading-relaxed">
          Verifica los datos en el sitio oficial del consulado antes de visitar. Los horarios y direcciones pueden cambiar.
        </p>
      </div>

      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
        <Input
          data-testid="consulates-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por país, ciudad o dirección…"
          className="h-12 pl-10 rounded-xl border-2 border-zinc-300"
        />
      </div>

      <div className="flex gap-2 overflow-x-auto pb-2 mb-4 -mx-1 px-1">
        <button
          data-testid="filter-country-all"
          onClick={() => setFilterCountry("all")}
          className={`flex-shrink-0 px-4 h-9 rounded-full text-xs font-bold transition-colors ${
            filterCountry === "all" ? "bg-zinc-900 text-white" : "bg-white border border-zinc-300 text-zinc-700"
          }`}
        >
          Todos
        </button>
        {countries.map((c) => (
          <button
            key={c}
            data-testid={`filter-country-${c}`}
            onClick={() => setFilterCountry(c)}
            className={`flex-shrink-0 px-4 h-9 rounded-full text-xs font-bold whitespace-nowrap transition-colors ${
              filterCountry === c ? "bg-zinc-900 text-white" : "bg-white border border-zinc-300 text-zinc-700"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-xl p-8 text-center text-zinc-500">
          No encontramos resultados con esos filtros.
        </div>
      ) : (
        <div className="grid gap-3">
          {items.map((c) => (
            <div
              key={c.id}
              data-testid={`consulate-${c.id}`}
              className="bg-white border-2 border-zinc-200 rounded-2xl p-5 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-blue-700">
                    {c.country}
                  </p>
                  <h3 className="font-black text-lg text-zinc-900 leading-tight">{c.city}</h3>
                </div>
                <Building2 className="w-6 h-6 text-zinc-300 flex-shrink-0" />
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-2 text-zinc-700">
                  <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-zinc-400" />
                  <span className="leading-snug">{c.address}</span>
                </div>
                {c.hours && (
                  <div className="flex items-center gap-2 text-zinc-700">
                    <Clock className="w-4 h-4 flex-shrink-0 text-zinc-400" />
                    <span>{c.hours}</span>
                  </div>
                )}
                {c.phone && (
                  <a
                    href={`tel:${c.phone.replace(/[^+\d]/g, "")}`}
                    className="flex items-center gap-2 text-blue-800 font-bold hover:underline"
                    data-testid={`consulate-call-${c.id}`}
                  >
                    <Phone className="w-4 h-4" />
                    {c.phone}
                  </a>
                )}
                {c.emergency_24h && c.emergency_24h !== c.phone && (
                  <a
                    href={`tel:${c.emergency_24h.replace(/[^+\d]/g, "")}`}
                    className="flex items-center gap-2 text-red-700 font-bold hover:underline text-xs"
                  >
                    <Phone className="w-3.5 h-3.5" />
                    Emergencia 24h: {c.emergency_24h}
                  </a>
                )}
                {c.website && (
                  <a
                    href={c.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-zinc-600 hover:text-zinc-900 text-xs font-semibold"
                  >
                    <Globe className="w-3.5 h-3.5" />
                    Sitio oficial
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* -------------------- LEGAL HELP TAB -------------------- */
function LegalTab({ items }) {
  return (
    <div>
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 mb-4 flex gap-2 items-start">
        <Scale className="w-4 h-4 text-emerald-700 mt-0.5 flex-shrink-0" />
        <p className="text-xs text-emerald-900 leading-relaxed">
          Todas las organizaciones listadas ofrecen servicios <span className="font-bold">100% GRATUITOS</span>. Algunas tienen líneas de emergencia directas para personas detenidas.
        </p>
      </div>

      <div className="grid gap-3">
        {items.map((o) => (
          <div
            key={o.id}
            data-testid={`legal-${o.id}`}
            className="bg-white border-2 border-zinc-200 rounded-2xl p-5 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex-1">
                <h3 className="font-black text-lg text-zinc-900 leading-tight">{o.name}</h3>
                <p className="text-xs text-zinc-500 mt-0.5">{o.scope}</p>
              </div>
              {o.free && (
                <span className="text-[10px] font-black uppercase tracking-widest text-emerald-700 bg-emerald-100 px-2 py-1 rounded-full whitespace-nowrap">
                  Gratis
                </span>
              )}
            </div>

            <p className="text-sm text-zinc-700 leading-snug mb-3">
              <span className="font-bold">Especialidad:</span> {o.focus}
            </p>

            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <Languages className="w-3.5 h-3.5 text-zinc-400" />
              {o.languages.map((lang) => (
                <span key={lang} className="text-[10px] font-bold text-zinc-700 bg-zinc-100 px-2 py-0.5 rounded-full">
                  {lang}
                </span>
              ))}
            </div>

            <div className="space-y-1.5 text-sm">
              {o.hotline && (
                <a
                  href={`tel:${o.hotline.replace(/[^+\d]/g, "")}`}
                  data-testid={`legal-hotline-${o.id}`}
                  className="flex items-center gap-2 text-red-700 font-black hover:underline"
                >
                  <Phone className="w-4 h-4" />
                  Hotline: {o.hotline}
                </a>
              )}
              {o.phone && o.phone !== o.hotline && (
                <a
                  href={`tel:${o.phone.replace(/[^+\d]/g, "")}`}
                  className="flex items-center gap-2 text-blue-800 font-bold hover:underline"
                >
                  <Phone className="w-4 h-4" />
                  {o.phone}
                </a>
              )}
              {o.website && (
                <a
                  href={o.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-zinc-600 hover:text-zinc-900 text-xs font-semibold"
                >
                  <Globe className="w-3.5 h-3.5" />
                  Visitar sitio
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>

            {o.notes && (
              <p className="text-xs text-zinc-600 mt-3 pt-3 border-t border-zinc-100 leading-snug italic">
                {o.notes}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* -------------------- NEWS TAB -------------------- */
function NewsTab({ sources, guidance }) {
  return (
    <div>
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-5 flex gap-2 items-start">
        <Info className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
        <p className="text-xs text-amber-900 leading-relaxed">
          Te enlazamos a fuentes verificadas. <span className="font-bold">NO publicamos ubicaciones de redadas en tiempo real</span> por seguridad legal y comunitaria.
        </p>
      </div>

      <h3 className="text-lg font-black text-zinc-900 mb-3">Fuentes confiables de noticias migratorias</h3>
      <div className="grid gap-3 mb-8">
        {sources.map((s) => (
          <a
            key={s.id}
            href={s.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`news-source-${s.id}`}
            className="bg-white border-2 border-zinc-200 rounded-xl p-4 hover:border-amber-400 hover:shadow-md transition-all flex items-start gap-3 group"
          >
            <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
              <Newspaper className="w-5 h-5 text-amber-700" strokeWidth={2.5} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-0.5">
                <h4 className="font-bold text-zinc-900 leading-tight">{s.title}</h4>
                <ExternalLink className="w-3.5 h-3.5 text-zinc-400 group-hover:text-amber-700" />
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] font-bold uppercase text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">{s.type}</span>
                <span className="text-[10px] font-bold text-zinc-600">{s.language}</span>
              </div>
              <p className="text-xs text-zinc-600 mt-1 leading-snug">{s.description}</p>
            </div>
          </a>
        ))}
      </div>

      {guidance && (
        <div className="bg-zinc-900 rounded-2xl p-6 text-white">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-red-600 flex items-center justify-center flex-shrink-0">
              <ShieldAlert className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <h3 className="font-black text-lg leading-tight">{guidance.title}</h3>
              <p className="text-xs text-zinc-400 mt-0.5">{guidance.subtitle}</p>
            </div>
          </div>

          <div className="bg-zinc-800/50 border-l-4 border-l-amber-500 p-3 rounded-r mb-4">
            <p className="text-xs text-amber-100 leading-snug">
              <Info className="inline w-3.5 h-3.5 mr-1" />
              {guidance.important_note}
            </p>
          </div>

          <p className="text-sm font-bold text-zinc-300 mb-2">Tus derechos como observador:</p>
          <ul className="space-y-2 mb-5">
            {guidance.rights.map((r) => (
              <li key={r} className="flex items-start gap-2 text-sm text-zinc-200">
                <Eye className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                <span className="leading-snug">{r}</span>
              </li>
            ))}
          </ul>

          <p className="text-sm font-bold text-zinc-300 mb-2">Dónde reportar (a organizaciones acreditadas):</p>
          <div className="space-y-2 mb-4">
            {guidance.where_to_report.map((w) => (
              <div key={w.name} className="bg-zinc-800 rounded-lg p-3">
                <p className="font-bold text-white text-sm">{w.name}</p>
                {w.phone && (
                  <a href={`tel:${w.phone.replace(/[^+\d]/g, "")}`} className="text-emerald-400 font-bold text-sm hover:underline">
                    {w.phone}
                  </a>
                )}
                {w.url && (
                  <a href={w.url} target="_blank" rel="noopener noreferrer" className="text-zinc-400 text-xs hover:text-white block">
                    {w.url}
                  </a>
                )}
              </div>
            ))}
          </div>

          <div className="bg-red-950/50 border border-red-800 rounded-lg p-3 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-red-100 leading-snug">{guidance.warning}</p>
          </div>
        </div>
      )}
    </div>
  );
}
